import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'

import Callout from 'modules/core/components/Callout'
import { DateNavigator } from 'modules/core/components/DateNavigator'
import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'

import MinutesToText from 'modules/attendance/components/MinutesToText'
import { useBankOfHoursMovements } from 'modules/attendance/hooks/useBankOfHoursMovements'

import Item from './components/Item'

interface Props {
  employeeId: number
  startOn?: string
  endOn?: string
  selectedStartDate: Date
  selectedEndDate: Date
  enableCustomRange: boolean
  showClosedCycleWarning: boolean
  onDateRangeChange?: (startDate: Date, endDate: Date) => void
}

const PAGINATION_PAGE_SIZE = 7
const DATE_NAVIGATOR_PERIODS: ('day' | 'week' | 'fortnight' | 'month')[] = [
  'day',
  'week',
  'fortnight',
  'month',
]

const BreakdownAccordion = ({
  employeeId,
  startOn,
  endOn,
  selectedStartDate,
  selectedEndDate,
  enableCustomRange,
  showClosedCycleWarning,
  onDateRangeChange,
}: Props) => {
  const isRangeEmpty = !startOn || !endOn

  const { isLoading, bankOfHoursMovements, totalMinutes } = useBankOfHoursMovements({
    employeeId,
    startOn,
    endOn,
    skip: isRangeEmpty,
  })

  const [page, setPage] = React.useState(1) //default page is 1
  const visibleBankOfHoursMovements = bankOfHoursMovements.slice(0, page * PAGINATION_PAGE_SIZE)
  const showLoadMore = visibleBankOfHoursMovements.length !== bankOfHoursMovements.length

  React.useEffect(() => {
    setPage(1)
  }, [startOn, endOn])

  const handleDateRangeChange = React.useCallback(
    (startDate: Date, endDate: Date) => {
      setPage(1)
      onDateRangeChange?.(startDate, endDate)
    },
    [onDateRangeChange]
  )

  return (
    <Box gap="s8" paddingY="s24">
      {enableCustomRange && (
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s8">
          <DateNavigator
            regularPeriods={DATE_NAVIGATOR_PERIODS}
            startDate={selectedStartDate}
            endDate={selectedEndDate}
            withRelativeDates={false}
            longMonth
            onDateRangeChange={(...args) => handleDateRangeChange(...args)}
          />
          <MinutesToText
            value={totalMinutes}
            size="400"
            weight="medium"
            withPlusSign={totalMinutes > 0}
          />
        </Box>
      )}
      {enableCustomRange && showClosedCycleWarning && (
        <Callout
          type="warning"
          description={i18n.t('clock_in.bank_of_hours.sidepanel.closed_period_warning')}
        />
      )}
      {isLoading ? (
        <Loading />
      ) : bankOfHoursMovements.length > 0 ? (
        <Box
          border={{
            all: {
              width: 's1',
              color: 'grey400',
              style: 'solid',
            },
          }}
          borderRadius={{
            all: 'abs008',
          }}
        >
          {visibleBankOfHoursMovements.map((movement) => (
            <Item key={movement.id} movement={movement} />
          ))}
          {showLoadMore && (
            <Box background="grey100">
              <Box alignSelf="end" paddingY="s16" paddingRight="s16">
                <Button type="secondary" onClick={() => setPage(page + 1)}>
                  {i18n.t('clock_in.bank_of_hours.sidepanel.load_more_button')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  )
}

export default BreakdownAccordion
